import type { AbilityData } from '@thottbot/shared';
import { FieldTable, Money, Prose, RefLink, RefList, type RefView } from './FieldTable';
import { TalentsforeverCredit } from '../../components/TalentsforeverCredit';

type Rank = NonNullable<AbilityData['ranks']>[number];

const RANK_COLUMNS: { label: string; key: keyof Rank }[] = [
  { label: 'Level', key: 'level' },
  { label: 'Cost', key: 'cost' },
  { label: 'Cast', key: 'castTime' },
  { label: 'Cooldown', key: 'cooldown' },
  { label: 'Range', key: 'range' },
  { label: 'Price', key: 'trainerCost' },
];

/** One row per rank; columns no rank fills are left out. */
function RanksTable({ ranks }: { ranks: Rank[] }) {
  const columns = RANK_COLUMNS.filter((c) => ranks.some((r) => r[c.key] !== undefined));
  const hasDescription = ranks.some((r) => r.description);
  return (
    <div className="table-scroll">
      <table className="grid ranks">
        <thead>
          <tr>
            <th scope="col">Rank</th>
            {columns.map((c) => (
              <th key={c.key} scope="col">
                {c.label}
              </th>
            ))}
            {hasDescription && <th scope="col">Description</th>}
          </tr>
        </thead>
        <tbody>
          {ranks.map((r) => (
            <tr key={r.rank}>
              <td>{r.rank}</td>
              {columns.map((c) => (
                <td key={c.key}>
                  {c.key === 'trainerCost' ? <Money copper={r.trainerCost} /> : r[c.key]}
                </td>
              ))}
              {hasDescription && (
                <td>
                  <Prose text={r.description} />
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Only references the API resolved to a page; unresolved trainers are never shown as text. */
function resolved(refs: unknown): RefView[] | undefined {
  const list = (refs as RefView[] | undefined)?.filter((r) => r.slug);
  return list?.length ? list : undefined;
}

export function AbilityFields({ data }: { data: AbilityData }) {
  const taughtBy = resolved(data.taughtBy);
  const learnedFrom = resolved(data.learnedFrom);
  const talent = data.kind === 'talent';
  const prerequisite = data.prerequisite as (RefView & { rank?: number }) | undefined;
  return (
    <FieldTable
      rows={[
        { label: 'Class', value: data.class },
        { label: 'Tree', value: data.tree },
        {
          label: 'Talent',
          value: talent && data.tier ? `Tier ${data.tier}${data.column ? `, column ${data.column}` : ''}` : undefined,
        },
        {
          label: 'Requires',
          value: prerequisite?.slug ? (
            <>
              <RefLink r={prerequisite} fallbackType="ability" />
              {prerequisite.rank ? ` (rank ${prerequisite.rank})` : ''}
            </>
          ) : undefined,
        },
        { label: talent ? 'Requires level' : 'Level', value: data.level },
        { label: 'Rank', value: data.rank },
        { label: 'Cost', value: data.cost },
        { label: 'Cast time', value: data.passive ? 'Passive' : data.castTime },
        { label: 'Cooldown', value: data.cooldown },
        { label: 'Range', value: data.range },
        { label: 'Description', value: <Prose text={data.description} /> },
        { label: 'Ranks', value: data.ranks?.length ? <RanksTable ranks={data.ranks} /> : undefined },
        { label: 'Taught by', value: taughtBy && <RefList refs={taughtBy} fallbackType="npc" /> },
        {
          label: 'Learned from',
          value: learnedFrom && <RefList refs={learnedFrom} fallbackType="quest" />,
        },
        { label: 'Starting spell for', value: data.startingFor?.join(', ') || undefined },
        { label: 'Talent data', value: talent ? <TalentsforeverCredit /> : undefined },
      ]}
    />
  );
}
